import { useState, useEffect } from "react";
import { useApi } from "../hooks/useApi";

const yen = (n) => Number(n || 0).toLocaleString("ja-JP") + "円";

const FIELDS_KEY = "nouen_fields";

const CROP_OPTIONS = [
  "水稲・米", "野菜（露地）", "野菜（施設）", "果樹",
  "畑作（麦・大豆等）", "畜産・飼料", "その他",
];

function loadFields() {
  try {
    const saved = JSON.parse(localStorage.getItem(FIELDS_KEY));
    if (Array.isArray(saved)) return saved;
  } catch { /* 破損データは初期値で上書き */ }
  return [];
}

export default function FieldScreen() {
  const [fields, setFields]     = useState(loadFields);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ name: "", area: "", crop: CROP_OPTIONS[0], cost: "", memo: "" });
  const [aiTip, setAiTip]       = useState("");
  const { call, loading } = useApi();

  useEffect(() => {
    localStorage.setItem(FIELDS_KEY, JSON.stringify(fields));
  }, [fields]);

  const totalArea = fields.reduce((s, f) => s + Number(f.area || 0), 0);
  const totalCost = fields.reduce((s, f) => s + Number(f.cost || 0), 0);

  const addField = () => {
    if (!form.name) return;
    setFields([...fields, { ...form, id: Date.now() }]);
    setForm({ name: "", area: "", crop: CROP_OPTIONS[0], cost: "", memo: "" });
    setShowForm(false);
  };

  const removeField = (id) => setFields(fields.filter((f) => f.id !== id));

  const getAiTip = async () => {
    const tip = await call({
      messages: [{
        role: "user",
        content: `農家の圃場（農地）データです。
${fields.map((f) => `・${f.name}：${f.area}a、作物：${f.crop}、年間コスト目安：${yen(f.cost)}${f.memo ? `（${f.memo}）` : ""}`).join("\n")}
合計面積：${totalArea}a、年間コスト合計：${yen(totalCost)}
この圃場構成を見て、活用できそうな補助金の方向性やコスト改善の観点から2〜3点、短くアドバイスしてください。税務の個別判断は避け、一般的なアドバイスのみ。`,
      }],
    });
    setAiTip(tip);
  };

  return (
    <div className="screen">
      <h2 className="screen-title">🗺️ 圃場（農地）管理</h2>

      {/* サマリー */}
      <div className="summary-grid">
        <div className="summary-card" style={{ background: "#e8f5e9" }}>
          <div className="summary-label">📐 合計面積</div>
          <div className="summary-val" style={{ color: "#2e7d32" }}>{totalArea}a</div>
        </div>
        <div className="summary-card" style={{ background: "#fff3e0" }}>
          <div className="summary-label">💸 年間コスト</div>
          <div className="summary-val" style={{ color: "#e65100" }}>{yen(totalCost)}</div>
        </div>
      </div>

      {/* AI分析 */}
      {fields.length > 0 && (
        <button className="ai-tip-btn" onClick={getAiTip} disabled={loading}>
          {loading ? "分析中..." : "🤖 AIに圃場活用アドバイスを聞く"}
        </button>
      )}
      {aiTip && (
        <div className="ai-tip-box">
          <div className="ai-tip-header">🌾 農援AIからのアドバイス</div>
          <p className="ai-tip-text">{aiTip}</p>
        </div>
      )}

      {/* 圃場ヘッダー */}
      <div className="ledger-header">
        <span className="ledger-title">圃場一覧</span>
        <button className="add-btn" onClick={() => setShowForm(!showForm)}>＋ 追加</button>
      </div>

      {/* 入力フォーム */}
      {showForm && (
        <div className="form-box">
          <div className="form-row">
            <label className="form-label">圃場の名前</label>
            <input className="form-input" placeholder="例：家の裏の田んぼ" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-row">
            <label className="form-label">面積（a）</label>
            <input type="number" className="form-input" placeholder="例：30" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
          </div>
          <div className="form-row">
            <label className="form-label">作物</label>
            <select className="form-input" value={form.crop} onChange={(e) => setForm({ ...form, crop: e.target.value })}>
              {CROP_OPTIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">年間コスト目安（円）</label>
            <input type="number" className="form-input" placeholder="例：150000" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
          </div>
          <div className="form-row">
            <label className="form-label">メモ</label>
            <input className="form-input" placeholder="例：水はけが悪い" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
          </div>
          <button className="primary-btn" onClick={addField}>登録する</button>
        </div>
      )}

      {/* 圃場リスト */}
      {fields.length === 0 && !showForm && (
        <p className="ai-tip-text" style={{ textAlign: "center", padding: "16px 0" }}>
          「＋ 追加」から圃場を登録すると、面積・コストの管理とAIアドバイスが使えます。
        </p>
      )}
      <div className="entries-list">
        {fields.map((f) => (
          <div key={f.id} className="entry-row">
            <div className="entry-left">
              <div className="entry-memo">
                {f.name}
                <span className="entry-cat">{f.crop}</span>
              </div>
              <div className="entry-date">
                {f.area || 0}a{f.memo ? `・${f.memo}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className="entry-amount" style={{ color: "#e65100" }}>{yen(f.cost)}/年</div>
              <button className="add-btn" style={{ background: "#fce4ec", color: "#c62828" }} onClick={() => removeField(f.id)}>削除</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
